import { Badge, Card, PageHeader } from "@nelurio/ui";
import { useAdminResource } from "../hooks";
import { EmptyHint, ErrorState, LoadingState } from "../components";
import { useI18n } from "@nelurio/i18n";

type ProductStat = {
  appSlug: string;
  subs: number;
  trials: number;
  premium: number;
  feedback: number;
  feedbackOpen: number;
};

/** Display names per app (same capitalization as the Users page filter chips). */
const APP_LABELS: Record<string, string> = {
  nelurio: "Nelurio",
  finello: "Finello",
  nexdeutsch: "NexDeutsch",
  waschhalle: "Washhalle"
};

export function ProductsPage() {
  const { t, formatNumber } = useI18n();
  const stats = useAdminResource<{ data: ProductStat[] }>("/products");
  const products = stats.data?.data ?? [];

  const productAreas: Record<string, string> = {
    nelurio: t("admin.products.areaNelurio"),
    finello: t("admin.products.areaFinello"),
    waschhalle: t("admin.products.areaWaschhalle"),
    nexdeutsch: t("admin.products.areaNexdeutsch"),
  };

  return (
    <div className="admin-page">
      <PageHeader
        title={t("admin.products")}
        subtitle={t("admin.products.subtitle")}
      />

      {stats.status === "error" && <ErrorState message={stats.error} onRetry={() => void stats.reload()} />}
      {stats.status === "loading" && <LoadingState rows={4} />}

      {stats.status === "ready" && (
        <div className="products-grid">
          {products.map((p) => {
            const area = productAreas[p.appSlug] ?? p.appSlug;
            return (
              <Card key={p.appSlug} className="product-card">
                <div className="product-card__head">
                  <strong>{APP_LABELS[p.appSlug] ?? p.appSlug}</strong>
                  <Badge tone={p.feedbackOpen > 0 ? "amber" : "green"}>
                    {p.feedbackOpen > 0 ? t("admin.products.openCount", { count: p.feedbackOpen }) : "OK"}
                  </Badge>
                </div>
                <small className="product-card__area">{area}</small>
                <div className="product-card__stats">
                  <div><small>{t("admin.overview.subsTotal")}</small><strong>{formatNumber(p.subs)}</strong></div>
                  <div><small>Trials</small><strong>{formatNumber(p.trials)}</strong></div>
                  <div><small>Premium</small><strong>{formatNumber(p.premium)}</strong></div>
                  <div><small>Feedback</small><strong>{formatNumber(p.feedback)}</strong></div>
                </div>
                <div className="product-card__links">
                  <a href={`/subscriptions?app=${p.appSlug}`}>{t("admin.products.viewSubs")}</a>
                  <a href={`/feedback?app=${p.appSlug}`}>{t("admin.products.viewFeedback")}</a>
                </div>
              </Card>
            );
          })}
          {products.length === 0 && (
            <EmptyHint>
              {t("admin.products.empty")}
              <small className="nlp-hint">Produkte erscheinen hier automatisch, sobald Apps Daten melden.</small>
            </EmptyHint>
          )}
        </div>
      )}
    </div>
  );
}
